
import { useState, useCallback } from 'react';
import { replaceLocationVariables, fetchLocationByName } from '@/utils/variableReplacer';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

export const usePostProcessing = () => {
  const [processedPost, setProcessedPost] = useState<BlogPost | null>(null);

  const processLocationVariables = useCallback(async (post: BlogPost, locationName: string) => {
    try {
      console.log('Processing location variables for:', locationName);
      const location = await fetchLocationByName(locationName);
      console.log('Location data found:', location);
      
      if (location) {
        const processedContent = {
          ...post,
          title: replaceLocationVariables(post.title, location),
          excerpt: post.excerpt ? replaceLocationVariables(post.excerpt, location) : null,
          content: replaceLocationVariables(post.content, location),
          author: replaceLocationVariables(post.author, location),
          categories: post.categories?.map(cat => replaceLocationVariables(cat, location)) || null,
          tags: post.tags?.map(tag => replaceLocationVariables(tag, location)) || null,
          seo_title: post.seo_title ? replaceLocationVariables(post.seo_title, location) : null,
          seo_description: post.seo_description ? replaceLocationVariables(post.seo_description, location) : null,
          seo_keywords: post.seo_keywords?.map(keyword => replaceLocationVariables(keyword, location)) || null,
        };
        console.log('Processed content:', processedContent);
        setProcessedPost(processedContent);
      } else {
        console.log('No location data found, keeping original');
        setProcessedPost(post);
      }
    } catch (error) {
      console.error('Error processing location variables:', error);
      setProcessedPost(post);
    }
  }, []);

  const calculateReadTime = useCallback((content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  }, []);

  return {
    processedPost,
    setProcessedPost,
    processLocationVariables,
    calculateReadTime
  };
};
